import { useEffect, useMemo } from "react";
import {
    GetUserCardDocument,
    GetUserCardQuery,
    GetUserCardQueryVariables,
    GetUsersCardsDocument,
    GetUsersCardsQuery,
    GetUsersCardsQueryVariables,
    GetUsersQuery,
    GetUsersQueryVariables,
    ListAgentChatQuery,
    User,
    useGetAgentsQuery,
} from "../../__generated__/api";
import client from "../../api";
import { LoggingHandler } from "../logging/LoggingContext";
import useCurrentSession, { AuthenticationContext } from "../session";

export default class UserCache {
    public static useUserCache(chatData: ListAgentChatQuery | undefined) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const cache = useMemo(() => new UserCache({}), []);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        cache.setAuthenticationContext(useCurrentSession());

        // eslint-disable-next-line react-hooks/rules-of-hooks
        const { data: agents } = useGetAgentsQuery();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            if (agents?.settings?.agents) {
                cache.setAgents(agents?.settings?.agents as User[]);
            }
        }, [agents?.settings?.agents]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            if (chatData) {
                cache.addChatData(chatData);
            }
        }, [chatData?.chats?.chats]);
        return cache;
    }

    private authenticationContext?: AuthenticationContext;

    private currentAgent?: User;

    private constructor(
        private data: Record<string, User>,
    ) {
        // IGNORE
    }

    private addChatData(chatData: ListAgentChatQuery) {
        chatData.chats?.chats
            .flatMap(chat => [chat.assignee, chat.creator])
            .filterNull()
            .dedupe(item => item.id, (left, right) => ({ ...left, ...right } as User))
            .forEach(item => {
                this.data[item.id] = {
                    ...this.data[item.id],
                    ...item,
                };
            });
    }

    tryGet(id: string) {
        return this.data[id];
    }

    private setAgents(agents: User[]) {
        this.currentAgent = agents.find(agent => agent.isCurrentlyLoggedInUser) ?? this.currentAgent;
        agents?.forEach(user => {
            this.data[user.id] = {
                ...this.data[user.id],
                ...user,
            };
        });
    }

    private setAuthenticationContext(session: AuthenticationContext) {
        this.authenticationContext = session;
    }

    public get currentUser() {
        return this.currentAgent ?? this.authenticationContext?.user;
    }

    public get currentPermissions() {
        return this.authenticationContext?.permissions;
    }

    async get(id: string) {
        const current = this.tryGet(id);
        if (current) {
            return current;
        }

        const user = await LoggingHandler.instance.wrap(async () => {
            const response = await client.query<GetUserCardQuery, GetUserCardQueryVariables>({
                query: GetUserCardDocument,
                variables: {
                    id,
                },
            });
            if (response.data.user) {
                this.data[id] = response.data.user as User;
            }
            return this.data[id];
        });
        return user;
    }

    async getUsers(...ids: string[]) {
        const missingIds = ids.filter(id => !this.tryGet(id));

        await LoggingHandler.instance.wrap(async () => {
            const response = await client.query<GetUsersCardsQuery, GetUsersCardsQueryVariables>({
                query: GetUsersCardsDocument,
                variables: {
                    ids: missingIds,
                },
            });
            response.data.users?.forEach(user => {
                this.data[user.id] = {
                    ...this.data[user.id],
                    ...user,
                };
            });
        });
        return ids.map(id => this.tryGet(id)).filterNull();
    }
}
